/**
 * @author jakubbujakowski & jakubmaslon
 * @since 2020-4-27
 */

import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";

import { IS_BROWSER } from "@config/consts";
import { ROUTES } from "@config/app.routing";
import { USER_NOTIFICATIONS_TEST_IDS } from "@config/testIds/userNotifications";

import { PROMO_CODE_NOTIFICATION } from "@typings/globals";
import { LOADING_STATE, ThunkActionDispatch } from "@typings/reduxThunkActions";

import { AsyncActionResult } from "@services/AsyncActionCreatorFactory";

import { showToast } from "@containers/Toastify";

import { useVoucherManager } from "@hooks/useVoucherManager";
import { useTranslate } from "@hooks/useTranslate";
import { useTelemetry } from "@hooks/useTelemetry";

import {
  fetchVoucher as fetchVoucherBooking,
  setVoucher as setBookingVoucher,
} from "@app/redux/booking/booking.actions";
import {
  getExecutionDate as getBookingExecutionDateTime,
  getPricingLoadingState as getBookingPricingLoadingState,
  getPricing as getBookingPricing,
  getHasServiceVouchers,
} from "@app/redux/booking/booking.selectors";
import {
  fetchVoucher as fetchVoucherEndOfTenancy,
  setVoucher as setEndOfTenancyVoucher,
} from "@app/redux/endOfTenancy/endOfTenancy.actions";
import {
  getIsPricingFetched as getIsEndOfTenancyPricingFetched,
  getPricing as getEndOfTenancyPricing,
} from "@app/redux/endOfTenancy/endOfTenancy.selectors";
import {
  fetchVoucher as fetchVoucherB2B,
  setVoucher as setB2BVoucher,
} from "@app/redux/b2b/b2b.actions";
import {
  getPricingLoadingState as getB2BPricingLoadingState,
  getPricing as getB2BPricing,
} from "@app/redux/b2b/b2b.selectors";

const LOCATION_WHITELIST: string[] = [
  ROUTES.BOOKING,
  ROUTES.BOOKING_CHECKOUT,

  ROUTES.END_OF_TENANCY,
  ROUTES.END_OF_TENANCY_CHECKOUT,

  ROUTES.B2B,
  ROUTES.B2B_CHECKOUT,
];

const VoucherInterceptor = (): null => {
  const dispatch = useDispatch<ThunkActionDispatch>();
  const location = useLocation();
  const translate = useTranslate();
  const voucherManger = useVoucherManager();
  const { sendEvent } = useTelemetry();

  const [didFetchVoucher, setDidFetchVoucher] = React.useState<boolean>(false);

  const voucherToken =
    voucherManger.getStoredDeeplinkVoucher() ??
    voucherManger.getVoucherFromURLParameter();

  // end of tenancy
  const isEndOfTenancyPricingFetched = useSelector(
    getIsEndOfTenancyPricingFetched,
  );
  const endOfTenancyPricing = useSelector(getEndOfTenancyPricing);

  // booking
  const bookingExecutionDateTime = useSelector(getBookingExecutionDateTime);
  const bookingPricingLoadingState = useSelector(getBookingPricingLoadingState);
  const bookingPricing = useSelector(getBookingPricing);
  const hasServiceVouchers = useSelector(getHasServiceVouchers);

  // b2b
  const b2bPricingLoadingState = useSelector(getB2BPricingLoadingState);
  const b2bPricing = useSelector(getB2BPricing);

  const IS_BOOKING = location.pathname.includes(ROUTES.BOOKING);
  const IS_END_OF_TENANCY = location.pathname.includes(ROUTES.END_OF_TENANCY);
  const IS_B2B = location.pathname.includes(ROUTES.B2B);

  const shouldFetchVoucher =
    IS_BROWSER &&
    LOCATION_WHITELIST.includes(location.pathname) &&
    !didFetchVoucher;

  const shouldFetchVoucherEndOfTenancy =
    IS_END_OF_TENANCY && isEndOfTenancyPricingFetched && shouldFetchVoucher;

  const shouldFetchVoucherBooking =
    IS_BOOKING &&
    bookingExecutionDateTime &&
    bookingPricingLoadingState === LOADING_STATE.SUCCEEDED &&
    shouldFetchVoucher &&
    //service vouchers cannot be comined with regular ones (PWA-1639)
    !hasServiceVouchers;

  const shouldFetchVoucherB2B =
    IS_B2B &&
    b2bPricingLoadingState === LOADING_STATE.SUCCEEDED &&
    shouldFetchVoucher;

  const clearVouchersFromLocalStorage = (): void => {
    voucherManger.removeStoredDeeplinkVoucher();
    voucherManger.removeStoredVoucher();
  };

  const responseHandler = React.useCallback(
    ({ onError, onSuccess }: AsyncActionResult) => {
      onSuccess(payload => {
        showToast(payload.data.translatedSuccessMessage, {
          type: "success",
          toastId: USER_NOTIFICATIONS_TEST_IDS.VOUCHER_APPLIED,
        });
      });
      onError(error => {
        sendEvent("voucher-apply-failed", {
          context: {
            voucher: voucherToken,
            place: "home-cleaning-cleaning-information",
          },
        });
        showToast(error.message, { type: "error" });
        voucherManger.removeStoredVoucher();
      });
    },
    [voucherManger],
  );

  // voucher fetching end of tenancy
  React.useEffect(() => {
    if (shouldFetchVoucherEndOfTenancy) {
      if (!endOfTenancyPricing.get("hasSpecialPricing")) {
        (async () => {
          // `fetch voucher with sessionStorage token` case
          if (voucherToken) {
            const response = await dispatch<Promise<AsyncActionResult>>(
              fetchVoucherEndOfTenancy(voucherToken),
            );
            setDidFetchVoucher(true);
            responseHandler(response);
          }
          // `newly fetched hasSpecialPricing has changed to false` case
          else {
            dispatch(
              setEndOfTenancyVoucher({
                voucherMessage:
                  endOfTenancyPricing.get("specialPricingTranslatedMessage") ??
                  "",
                voucherStatus: PROMO_CODE_NOTIFICATION.NONE,
              }),
            );
          }
        })();
      } else {
        dispatch(
          setEndOfTenancyVoucher({
            voucherMessage:
              endOfTenancyPricing.get("specialPricingTranslatedMessage") ||
              translate("batmaid_pwa_end_of_tenancy.special_pricing_message"),
            voucherStatus: PROMO_CODE_NOTIFICATION.SUCCESS,
          }),
        );
      }
    }
  }, [
    location.pathname,
    isEndOfTenancyPricingFetched,
    endOfTenancyPricing,
    shouldFetchVoucherEndOfTenancy,
  ]);

  // voucher fetching booking
  React.useEffect(() => {
    if (shouldFetchVoucherBooking) {
      if (!bookingPricing.get("hasSpecialPricing")) {
        (async () => {
          // `fetch voucher with sessionStorage token` case
          if (voucherToken) {
            const response = await dispatch<Promise<AsyncActionResult>>(
              fetchVoucherBooking({ token: voucherToken }),
            );
            setDidFetchVoucher(true);
            responseHandler(response);
          }
          // `newly fetched hasSpecialPricing has changed to false` case
          else {
            dispatch(
              setBookingVoucher({
                voucherMessage:
                  bookingPricing.get("specialPricingTranslatedMessage") || "",
                voucherStatus: PROMO_CODE_NOTIFICATION.NONE,
              }),
            );
          }
        })();
      } else {
        dispatch(
          setBookingVoucher({
            voucherMessage:
              bookingPricing.get("specialPricingTranslatedMessage") ||
              translate("batmaid_pwa_booking.special_pricing_message"),
            voucherStatus: PROMO_CODE_NOTIFICATION.SUCCESS,
          }),
        );
      }
    }
  }, [
    location.pathname,
    bookingExecutionDateTime,
    bookingPricingLoadingState,
    bookingPricing,
    shouldFetchVoucherBooking,
  ]);

  // voucher fetching b2b
  React.useEffect(() => {
    if (shouldFetchVoucherB2B) {
      if (!b2bPricing.get("hasSpecialPricing")) {
        (async () => {
          // `fetch voucher with sessionStorage token` case
          if (voucherToken) {
            const response = await dispatch<Promise<AsyncActionResult>>(
              fetchVoucherB2B(voucherToken),
            );
            setDidFetchVoucher(true);
            responseHandler(response);
          }
          // `newly fetched hasSpecialPricing has changed to false` case
          else {
            dispatch(
              setB2BVoucher({
                voucherMessage:
                  b2bPricing.get("specialPricingTranslatedMessage") ?? "",
                voucherStatus: PROMO_CODE_NOTIFICATION.NONE,
              }),
            );
          }
        })();
      } else {
        dispatch(
          setB2BVoucher({
            voucherMessage:
              b2bPricing.get("specialPricingTranslatedMessage") ||
              translate("batmaid_pwa_b2b.special_pricing_message"),
            voucherStatus: PROMO_CODE_NOTIFICATION.SUCCESS,
          }),
        );
      }
    }
  }, [
    location.pathname,
    b2bPricingLoadingState,
    b2bPricing,
    shouldFetchVoucherB2B,
  ]);

  // voucher purging on route change
  React.useEffect(() => {
    if (!LOCATION_WHITELIST.includes(location.pathname) && didFetchVoucher) {
      voucherManger.removeStoredDeeplinkVoucher();
    }
  }, [location.pathname]);

  // voucher purging on app close
  React.useEffect(() => {
    window.addEventListener("beforeunload", clearVouchersFromLocalStorage);

    return () => {
      window.removeEventListener("beforeunload", clearVouchersFromLocalStorage);
    };
  }, []);

  return null;
};

export { VoucherInterceptor };
